/**
 * Công cụ đọc điểm ảnh của Bộ soi ảnh: biểu đồ sáng, báo cháy sáng / mất đen, ống hút màu, lớp
 * "khác nhau ở đâu" (spec 2026-10-28, mục 4.3 — học từ Lightroom).
 *
 * Tệp thuần trên mảng RGBA (`ImageData.data`), không chạm DOM: bài kiểm dựng mảng tay.
 */

import type { Size } from "./viewport";

export type Histogram = { l: Uint32Array; r: Uint32Array; g: Uint32Array; b: Uint32Array; total: number };

/** Độ sáng cảm nhận (Rec. 709), làm tròn về 0..255. */
function luma(r: number, g: number, b: number): number {
  return Math.round(0.2126 * r + 0.7152 * g + 0.0722 * b);
}

/** 256 ô mỗi kênh. Điểm trong suốt hẳn (alpha 0) không tính — đó là nền, không phải ảnh. */
export function histogram(px: Uint8ClampedArray): Histogram {
  const l = new Uint32Array(256);
  const r = new Uint32Array(256);
  const g = new Uint32Array(256);
  const b = new Uint32Array(256);
  let total = 0;
  for (let i = 0; i + 3 < px.length; i += 4) {
    if (px[i + 3] === 0) continue;
    const R = px[i]!;
    const G = px[i + 1]!;
    const B = px[i + 2]!;
    r[R]!++;
    g[G]!++;
    b[B]!++;
    l[luma(R, G, B)]!++;
    total++;
  }
  return { l, r, g, b, total };
}

/**
 * Lớp báo: ĐỎ ở điểm có một kênh ≥ `hi` (cháy sáng — mất chi tiết vùng sáng), XANH ở điểm mọi
 * kênh ≤ `lo` (mất chi tiết vùng tối); còn lại trong suốt.
 */
export function clippingMask(px: Uint8ClampedArray, hi = 250, lo = 5): Uint8ClampedArray {
  const out = new Uint8ClampedArray(px.length);
  for (let i = 0; i + 3 < px.length; i += 4) {
    const R = px[i]!;
    const G = px[i + 1]!;
    const B = px[i + 2]!;
    if (R >= hi || G >= hi || B >= hi) {
      out[i] = 255;
      out[i + 3] = 200;
    } else if (R <= lo && G <= lo && B <= lo) {
      out[i + 2] = 255;
      out[i + 1] = 120;
      out[i + 3] = 200;
    }
  }
  return out;
}

/**
 * Lớp "khác nhau ở đâu": tô đỏ điểm mà tổng lệch ba kênh vượt `threshold`. Hai mảng phải cùng cỡ
 * (người gọi vẽ hai bản lên canvas cùng kích thước). Trả cả số điểm lệch để màn ghi "lệch 3,2%".
 */
export function diffMask(
  a: Uint8ClampedArray,
  b: Uint8ClampedArray,
  threshold = 36,
): { mask: Uint8ClampedArray; changed: number } {
  const n = Math.min(a.length, b.length);
  const mask = new Uint8ClampedArray(n);
  let changed = 0;
  for (let i = 0; i + 3 < n; i += 4) {
    const d = Math.abs(a[i]! - b[i]!) + Math.abs(a[i + 1]! - b[i + 1]!) + Math.abs(a[i + 2]! - b[i + 2]!);
    if (d > threshold) {
      mask[i] = 255;
      mask[i + 1] = 40;
      mask[i + 2] = 40;
      mask[i + 3] = Math.min(230, 90 + d);
      changed++;
    }
  }
  return { mask, changed };
}

const hex2 = (n: number) => n.toString(16).padStart(2, "0");

/** Màu trung bình ô (2·radius+1)² quanh (x, y); ô chạm mép thì chỉ lấy phần trong ảnh. */
export function sampleColor(
  px: Uint8ClampedArray,
  w: number,
  h: number,
  x: number,
  y: number,
  radius = 1,
): { r: number; g: number; b: number; hex: string } {
  let R = 0;
  let G = 0;
  let B = 0;
  let n = 0;
  const cx = Math.round(x);
  const cy = Math.round(y);
  for (let yy = Math.max(0, cy - radius); yy <= Math.min(h - 1, cy + radius); yy++) {
    for (let xx = Math.max(0, cx - radius); xx <= Math.min(w - 1, cx + radius); xx++) {
      const i = (yy * w + xx) * 4;
      R += px[i]!;
      G += px[i + 1]!;
      B += px[i + 2]!;
      n++;
    }
  }
  if (n === 0) return { r: 0, g: 0, b: 0, hex: "#000000" };
  const r = Math.round(R / n);
  const g = Math.round(G / n);
  const b = Math.round(B / n);
  return { r, g, b, hex: `#${hex2(r)}${hex2(g)}${hex2(b)}` };
}

/** Hai ảnh cùng tỉ lệ khung (bản sửa không cắt lại) thì mới so từng điểm được. */
export function sameAspect(a: Size, b: Size, tolerance = 0.01): boolean {
  if (a.w <= 0 || a.h <= 0 || b.w <= 0 || b.h <= 0) return false;
  return Math.abs(a.w / a.h - b.w / b.h) <= tolerance * (a.w / a.h);
}

/** Cắt vùng `rect` (phần trăm ảnh) ra khỏi mảng điểm — biểu đồ sáng tính trên vùng đang xem. */
export function cropPixels(
  px: Uint8ClampedArray,
  w: number,
  h: number,
  rect: { x: number; y: number; w: number; h: number },
): Uint8ClampedArray {
  const x0 = Math.max(0, Math.floor(rect.x * w));
  const y0 = Math.max(0, Math.floor(rect.y * h));
  const x1 = Math.min(w, Math.ceil((rect.x + rect.w) * w));
  const y1 = Math.min(h, Math.ceil((rect.y + rect.h) * h));
  if (x1 <= x0 || y1 <= y0) return new Uint8ClampedArray(0);
  const out = new Uint8ClampedArray((x1 - x0) * (y1 - y0) * 4);
  let o = 0;
  for (let y = y0; y < y1; y++) {
    const start = (y * w + x0) * 4;
    out.set(px.subarray(start, start + (x1 - x0) * 4), o);
    o += (x1 - x0) * 4;
  }
  return out;
}
